import { ref, type InjectionKey, type Ref } from 'vue'
import en from './i18n/en.json'
import fr from './i18n/fr.json'
import { injectStrict } from './utils/inject'

export type LocaleDict = Record<string, string>

const DEFAULT_LOCALE = 'fr'
const MESSAGES: Record<string, LocaleDict> = { fr, en }

export interface I18n {
  t(key: string, params?: Record<string, string | number>): string
  locale: Ref<string>
}

export const I18N_KEY: InjectionKey<I18n> = Symbol('fg-widget-i18n')

export function createI18n(initialLocale: string = DEFAULT_LOCALE): I18n {
  const locale = ref(initialLocale)
  return {
    locale,
    t(key, params) {
      const template = MESSAGES[locale.value]?.[key] ?? MESSAGES[DEFAULT_LOCALE]?.[key]
      if (template === undefined) {
        warnMissingKey(key, locale.value)
        return key
      }
      return interpolate(template, params)
    },
  }
}

export function useI18n(): I18n {
  return injectStrict(I18N_KEY, 'I18n')
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = params[name]
    return value != null ? String(value) : `{${name}}`
  })
}

const warned = new Set<string>()
function warnMissingKey(key: string, locale: string): void {
  const id = `${locale}:${key}`
  if (warned.has(id)) return
  warned.add(id)
  console.warn(`[widget] missing i18n key "${key}" (locale=${locale})`)
}
